import { ArrowClockwise, ChatCircle, Lightning, Sparkle, Warning } from "@phosphor-icons/react";
import { Node } from "@tiptap/core";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

/**
 * The instruction block — a prose-native way to ask for work.
 *
 * Type `[summarize the above in 3 points]` on a line of its own and it becomes a card. The
 * product rule the card exists to enforce is in its `complete` state: **the result never
 * applies itself.** A finished instruction shows what it would write and waits for a person to
 * press Apply. That is the difference between an assistant and an autopilot.
 *
 * ⚠ `targetThinkerIds` is a JSON STRING, like every other list attr in this editor: ProseMirror
 * and Yjs attrs are scalars, and an object attr is rewritten into the CRDT on every keystroke.
 */

export type InstructionStatus = "pending" | "processing" | "complete" | "error";

export interface InstructionBlockOptions {
  /** Applies a completed result in place of the card. */
  onApply?: (info: { pos: number; result: string }) => void;
  /** Re-runs a failed instruction. */
  onRetry?: (info: { pos: number; instruction: string }) => void;
  /** Opens the conversation an instruction produced (a later wave owns the drawer). */
  onOpenConversation?: (conversationId: string) => void;
}

/**
 * Splits a finished instruction line into its parts.
 *
 * `@name` mentions address specific thinkers; a trailing `, others review` (or
 * `, review by others`) asks for a second pair of eyes. Both are STRIPPED from the instruction
 * text — what the model receives is the request, not the routing.
 */
export function parseInstructionMentions(text: string): {
  instruction: string;
  thinkerMentions: string[];
  reviewRequested: boolean;
} {
  let working = text.trim();
  const reviewRequested = /,\s*(others\s+review|review\s+by\s+others)\s*$/i.test(working);
  working = working.replace(/,\s*(others\s+review|review\s+by\s+others)\s*$/i, "").trim();

  const thinkerMentions: string[] = [];
  working = working
    .replace(/@([a-zA-Z][a-zA-Z0-9_-]*)/g, (_match, name: string) => {
      thinkerMentions.push(name);
      return "";
    })
    .replace(/\s{2,}/g, " ")
    .trim();

  return { instruction: working, thinkerMentions, reviewRequested };
}

function InstructionCard(props: NodeViewProps) {
  const attrs = props.node.attrs as {
    instruction: string;
    status: InstructionStatus;
    conversationId: string | null;
    resultText: string | null;
    errorMessage: string | null;
  };
  const options = props.extension.options as InstructionBlockOptions;
  const pos = typeof props.getPos === "function" ? (props.getPos() ?? 0) : 0;

  return (
    <NodeViewWrapper as="div" contentEditable={false}>
      <div className="flex flex-col gap-2 rounded-md border border-divider bg-bg-secondary p-3">
        <div className="flex items-center gap-2">
          <StatusIcon status={attrs.status} />
          <span className="min-w-0 flex-1 text-body text-text">[{attrs.instruction}]</span>
        </div>

        {attrs.status === "complete" && attrs.resultText ? (
          <>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-sm bg-bg p-2 text-small text-text">
              {attrs.resultText}
            </pre>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => options.onApply?.({ pos, result: attrs.resultText ?? "" })}
                className="rounded-sm bg-accent px-2 py-1 text-small font-medium text-accent-text"
              >
                Apply to document
              </button>
              {attrs.conversationId ? (
                <button
                  type="button"
                  onClick={() => options.onOpenConversation?.(attrs.conversationId ?? "")}
                  className="flex items-center gap-1 rounded-sm px-2 py-1 text-small text-text-secondary hover:bg-hover-bg"
                >
                  <ChatCircle size={14} aria-hidden />
                  View conversation
                </button>
              ) : null}
            </div>
          </>
        ) : null}

        {attrs.status === "error" ? (
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 text-small text-danger">
              {attrs.errorMessage ?? "This instruction failed."}
            </span>
            <button
              type="button"
              onClick={() => options.onRetry?.({ pos, instruction: attrs.instruction })}
              className="flex items-center gap-1 rounded-sm px-2 py-1 text-small text-text-secondary hover:bg-hover-bg"
            >
              <ArrowClockwise size={14} aria-hidden />
              Retry
            </button>
          </div>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

function StatusIcon({ status }: { status: InstructionStatus }) {
  if (status === "processing") {
    return <Lightning size={16} aria-hidden className="animate-pulse text-accent" />;
  }
  if (status === "error") {
    return <Warning size={16} aria-hidden className="text-danger" />;
  }
  return <Sparkle size={16} aria-hidden className="text-accent" />;
}

export const InstructionBlockNode = Node.create<InstructionBlockOptions>({
  name: "instruction_block",
  group: "block",
  atom: true,
  defining: true,
  draggable: true,

  addOptions() {
    return {};
  },

  addAttributes() {
    return {
      instruction: { default: "" },
      /** JSON string — never an array (Yjs attr identity, see the file header). */
      targetThinkerIds: { default: "[]" },
      reviewRequested: { default: false },
      status: { default: "pending" as InstructionStatus },
      conversationId: { default: null },
      resultText: { default: null },
      errorMessage: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-instruction]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { "data-instruction": String(HTMLAttributes.instruction ?? "") }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InstructionCard);
  },

  renderMarkdown(node) {
    const attrs = (node.attrs ?? {}) as {
      instruction?: string;
      targetThinkerIds?: string;
      reviewRequested?: boolean;
      status?: InstructionStatus;
      conversationId?: string | null;
      resultText?: string | null;
      errorMessage?: string | null;
    };
    const instruction = attrs.instruction ?? "";

    // Never run: the bare bracketed form, with the routing put back the way it was written.
    if (!attrs.conversationId) {
      const mentions = safeList(attrs.targetThinkerIds)
        .map((id) => `@${id}`)
        .join(" ");
      const review = attrs.reviewRequested ? ", others review" : "";
      return `[${[mentions, instruction].filter(Boolean).join(" ")}${review}]`;
    }

    const params = new URLSearchParams();
    params.set("instruction", instruction);
    params.set("targetThinkerIds", attrs.targetThinkerIds ?? "[]");
    params.set("reviewRequested", String(attrs.reviewRequested === true));
    params.set("status", attrs.status ?? "complete");
    if (attrs.resultText) {
      params.set("resultText", attrs.resultText);
    }
    if (attrs.errorMessage) {
      params.set("errorMessage", attrs.errorMessage);
    }
    return `[${instruction}](instruction://${attrs.conversationId}?${params.toString()})`;
  },
});

function safeList(raw: unknown): string[] {
  if (typeof raw !== "string") {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}
